import React, { useState, useEffect, useMemo, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import StepperComponent from '../components/ui/StepperComponent';
import CartReviewStep from '../components/checkout/CartReviewStep';
import ShippingDetailsStep from '../components/checkout/ShippingDetailsStep';
import PaymentErrorBoundary from '../components/error/PaymentErrorBoundary';
import { useCart } from '../contexts/CartContext';
import { useCreateOrder } from '../hooks/queries/useOrderQueries';
import { toast } from 'react-hot-toast';
import paymentService from '../services/paymentService';

// Helper to calculate the effective (discounted) price of a product
const getEffectivePrice = (product) => {
  if (!product) return 0;

  const now = new Date();

  if (product.isNearExpiry && product.expiryDate && product.nearExpiryDiscountValue) {
    const expiryDate = new Date(product.expiryDate);
    if (now <= expiryDate) {
      if (product.nearExpiryDiscountType === 'percentage') {
        return Math.round(product.regularPrice * (1 - product.nearExpiryDiscountValue / 100));
      } else {
        return product.regularPrice - product.nearExpiryDiscountValue;
      }
    }
  }

  const isSaleActive = product.isSaleActive &&
    product.discountPercentage &&
    product.saleStartDate &&
    product.saleEndDate &&
    now >= new Date(product.saleStartDate) &&
    now <= new Date(product.saleEndDate);

  if (isSaleActive) {
    const salePrice = product.regularPrice * (1 - product.discountPercentage / 100);
    return Math.round(salePrice);
  }

  if (product.specialOfferPrice && product.specialOfferPrice < product.regularPrice) {
    return product.specialOfferPrice;
  }

  if (product.discountValue > 0 && product.regularPrice) {
    if (product.discountType === 'percentage') {
      return product.regularPrice * (1 - product.discountValue / 100);
    } else {
      return product.regularPrice - product.discountValue;
    }
  }

  return product.regularPrice || 0;
};

// Loading components for Suspense
const CheckoutSkeleton = () => (
  <div className="min-h-screen bg-gray-50 py-8">
    <div className="container mx-auto px-4 max-w-3xl">
      {/* Stepper Skeleton */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[1, 2].map((step) => (
            <div key={step} className="flex items-center">
              <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse" />
              {step < 2 && <div className="w-16 h-0.5 bg-gray-200 mx-2 animate-pulse" />}
            </div>
          ))}
        </div>
      </div>
      
      {/* Content Skeleton */}
      <div className="bg-white rounded-lg shadow-mobile p-6">
        <div className="space-y-4">
          <div className="h-6 bg-gray-200 rounded animate-pulse w-1/3" />
          <div className="h-4 bg-gray-200 rounded animate-pulse w-2/3" />
          <div className="h-32 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
    </div>
  </div>
);

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    availableItems = [],
    formatPrice,
    loading: cartLoading,
    isInitialized,
    clearCart,
    fetchCart
  } = useCart();

  const createOrderMutation = useCreateOrder();

  // Items the customer explicitly selected on the cart page for checkout.
  // Falls back to all available items when there's no selection in nav state
  // (e.g. direct link or page refresh), preserving the previous behavior.
  const selectedItemIds = location.state?.selectedItemIds;
  const checkoutItems = useMemo(() => {
    return Array.isArray(selectedItemIds) && selectedItemIds.length > 0
      ? availableItems.filter(item => selectedItemIds.includes(item._id))
      : availableItems;
  }, [availableItems, selectedItemIds]);

  const itemCount = checkoutItems.reduce((total, item) => total + item.quantity, 0);
  const totalValue = useMemo(() => {
    return checkoutItems.reduce((total, item) => total + (getEffectivePrice(item.product) * item.quantity), 0);
  }, [checkoutItems]);

  const [currentStep, setCurrentStep] = useState(1);
  const [orderData, setOrderData] = useState({
    cart: [],
    shipping: null,
    payment: null
  });
  const [processingProgress, setProcessingProgress] = useState(0);

  // Refresh cart on mount so checkout reflects live stock/status
  useEffect(() => {
    if (fetchCart) {
      fetchCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirect to cart if no items or not initialized
  useEffect(() => {
    if (isInitialized && !cartLoading && checkoutItems.length === 0) {
      toast.error('Your cart has no available items. Please add items before checkout.');
      navigate('/cart');
    }
  }, [checkoutItems, isInitialized, cartLoading, navigate]);

  useEffect(() => {
    // Update order data with cart items
    setOrderData(prev => ({
      ...prev,
      cart: checkoutItems
    }));
  }, [checkoutItems]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      paymentService.cleanup();
    };
  }, []);

  // Step navigation handlers
  const handleCartNext = () => {
    setCurrentStep(2);
  };

  const handleShippingBack = () => {
    setCurrentStep(1);
  };

  // Shipping and payment are one combined step - this fires once the user
  // submits both at the same time, and goes straight into placing the order.
  const handleCheckoutSubmit = async (combinedData) => {
    try {
      if (!combinedData || !combinedData.fullName || !combinedData.email) {
        toast.error('Please fill in all required shipping details');
        return;
      }

      const requiredFields = ['fullName', 'phone', 'email', 'address', 'city'];
      const missingFields = requiredFields.filter(field => !combinedData[field]);

      if (missingFields.length > 0) {
        toast.error(`Missing required fields: ${missingFields.join(', ')}`);
        return;
      }

      const paymentData = {
        paymentMethod: combinedData.paymentMethod,
        screenshotFile: combinedData.screenshotFile || null
      };

      if (!paymentData.paymentMethod) {
        toast.error('Please select a payment method');
        return;
      }

      if (paymentData.paymentMethod === 'pay-now' && !paymentData.screenshotFile) {
        toast.error('Please upload a payment screenshot for online payment');
        return;
      }

      const shipping = {
        fullName: combinedData.fullName?.trim() || '',
        phone: combinedData.phone?.trim() || '',
        email: combinedData.email?.trim() || '',
        address: combinedData.address?.trim() || '',
        city: combinedData.city?.trim() || '',
        panNumber: combinedData.panNumber?.trim() || '',
        clinicName: combinedData.clinicName?.trim() || '',
        orderNote: combinedData.orderNote?.trim() || ''
      };

      const completeOrderData = {
        ...orderData,
        shipping,
        payment: paymentData
      };
      setOrderData(completeOrderData);

      // Progress callback for enhanced UX
      const progressCallback = ({ progress, message }) => {
        setProcessingProgress(progress);
        if (message) {
          toast.loading(message, { id: 'order-progress' });
        }
      };

      // Show initial loading toast
      toast.loading('Preparing your order...', { id: 'order-progress' });
      setProcessingProgress(5);
      
      try {
        // Use the enhanced payment service for order creation
        const result = await paymentService.createOrder(
          {
            shipping: completeOrderData.shipping,
            payment: paymentData,
            selectedItemIds: checkoutItems.map(item => item._id)
          },
          paymentData.screenshotFile,
          progressCallback
        );
        
        // Dismiss loading toast
        toast.dismiss('order-progress');
        setProcessingProgress(100);
        
        if (result && result.success) {
          // Clear the cart after successful order
          await clearCart();
          
          // Navigate to success page with order data
          navigate('/order-success', { 
            state: { 
              fromCheckout: true,
              orderData: result.data
            }
          });
        }
      } catch (error) {
        // Dismiss loading toast
        toast.dismiss('order-progress');
        setProcessingProgress(0);
        
        console.error('Order creation failed:', error);
        
        // Show specific error message
        const errorMessage = error.message || 'Failed to create order. Please try again.';
        toast.error(errorMessage);

        // Only add the connectivity hint for genuine network-ish failures -
        // any other error (stock, validation, etc.) already has its own
        // specific message above and doesn't need a misleading second toast.
        if (error.name === 'NetworkError' || error.message?.includes('Network') || error.name === 'TimeoutError') {
          toast.error('Please check your internet connection and try again.');
        }
      }
    } catch (error) {
      console.error('Error in handleCheckoutSubmit:', error);
      toast.error('An unexpected error occurred. Please try again.');
      setProcessingProgress(0);
    }
  };

  // Calculate order summary with validation
  const calculateOrderSummary = () => {
    const subtotal = totalValue || 0;
    // Rs. 100 fixed shipping, free on orders of Rs. 5000 or more
    const shipping = subtotal >= 5000 ? 0 : 100;
    return {
      subtotal,
      shipping,
      total: subtotal + shipping
    };
  };

  // Show loading skeleton while cart is loading
  if (cartLoading || !isInitialized) {
    return <CheckoutSkeleton />;
  }

  // Early return if no items (will trigger redirect effect)
  if (checkoutItems.length === 0) {
    return <CheckoutSkeleton />;
  }

  const orderSummary = calculateOrderSummary();

  return (
    <PaymentErrorBoundary
      onRetry={() => {
        // Reset form state on error boundary reset
        setCurrentStep(1);
        setProcessingProgress(0);
        setOrderData({
          cart: checkoutItems,
          shipping: null,
          payment: null
        });
      }}
    >
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-3xl pb-24 lg:pb-0">
          {/* Progress Stepper */}
          <div className="mb-8">
            <StepperComponent currentStep={currentStep} />
          </div>

          {/* Processing Progress Bar */}
          {processingProgress > 0 && processingProgress < 100 && (
            <div className="mb-6 bg-white rounded-lg p-4 shadow-mobile">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Processing your order...</span>
                <span>{Math.round(processingProgress)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-primary-500 h-2 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${processingProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Main Content */}
          <Suspense fallback={<CheckoutSkeleton />}>
            {currentStep === 1 && (
              <CartReviewStep
                items={checkoutItems}
                totalValue={totalValue}
                formatPrice={formatPrice}
                onNext={handleCartNext}
                orderSummary={orderSummary}
              />
            )}

            {currentStep === 2 && (
              <ShippingDetailsStep
                onBack={handleShippingBack}
                onComplete={handleCheckoutSubmit}
                initialData={orderData.shipping}
                orderSummary={orderSummary}
                isSubmitting={createOrderMutation.isPending || processingProgress > 0}
              />
            )}
          </Suspense>

          {/* Order Summary Sticky Bar (Mobile) */}
          <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-10">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">Total ({itemCount} items)</p>
                <p className="text-lg font-semibold text-gray-900">
                  Rs. {formatPrice(orderSummary.total)}
                </p>
              </div>
              {currentStep < 2 && (
                <div className="text-sm text-gray-500">
                  Step {currentStep} of 2
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PaymentErrorBoundary>
  );
};

export default CheckoutPage;